import type { BoardSummaryDto } from '@miroom/shared';
import {
  Copy,
  ExternalLink,
  LogOut,
  MoreHorizontal,
  Pencil,
  RotateCcw,
  Star,
  Trash2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePrefs } from '../../../application/stores/prefsStore';
import { useSession } from '../../../application/stores/sessionStore';
import { Avatar } from '../../components/Avatar';
import { IconButton } from '../../components/Button';
import { Menu, type MenuEntry } from '../../components/Menu';
import { relativeTime, useT } from '../../i18n/i18n';
import styles from './Dashboard.module.css';

export interface BoardCardActions {
  onStar(b: BoardSummaryDto): void;
  onRename(b: BoardSummaryDto): void;
  onDuplicate(b: BoardSummaryDto): void;
  onRemove(b: BoardSummaryDto): void;
  onRestore(b: BoardSummaryDto): void;
  onDestroy(b: BoardSummaryDto): void;
}

/** Deterministic pastel background for boards without a thumbnail. */
function placeholderHue(id: string) {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) % 360;
  return h;
}

export function BoardCard({
  board,
  actions,
}: {
  board: BoardSummaryDto;
  actions: BoardCardActions;
}) {
  const t = useT();
  const locale = usePrefs((s) => s.locale);
  const me = useSession((s) => s.user);
  const inTrash = board.deletedAt !== null;
  const isOwner = board.role === 'owner';
  const href = `/b/${board.id}`;

  const items: MenuEntry[] = inTrash
    ? [
        {
          label: t('dashboard.restore'),
          icon: <RotateCcw size={16} />,
          onSelect: () => actions.onRestore(board),
        },
        {
          label: t('dashboard.deleteForever'),
          icon: <Trash2 size={16} />,
          danger: true,
          onSelect: () => actions.onDestroy(board),
        },
      ]
    : [
        {
          label: t('dashboard.openNewTab'),
          icon: <ExternalLink size={16} />,
          onSelect: () => window.open(href, '_blank', 'noopener'),
        },
        ...(board.role === 'owner' || board.role === 'editor'
          ? [
              {
                label: t('dashboard.rename'),
                icon: <Pencil size={16} />,
                onSelect: () => actions.onRename(board),
              },
            ]
          : []),
        {
          label: t('dashboard.duplicate'),
          icon: <Copy size={16} />,
          onSelect: () => actions.onDuplicate(board),
        },
        'separator',
        isOwner
          ? {
              label: t('dashboard.moveToTrash'),
              icon: <Trash2 size={16} />,
              danger: true,
              onSelect: () => actions.onRemove(board),
            }
          : {
              label: t('dashboard.leave'),
              icon: <LogOut size={16} />,
              danger: true,
              onSelect: () => actions.onRemove(board),
            },
      ];

  const hue = placeholderHue(board.id);
  const preview = (
    <span
      className={styles.thumb}
      style={board.thumbnail ? undefined : { background: `hsl(${hue} 70% 92%)` }}
    >
      {board.thumbnail ? (
        <img src={board.thumbnail} alt="" loading="lazy" />
      ) : (
        <span className={styles.thumbLetter} style={{ color: `hsl(${hue} 50% 45%)` }}>
          {board.title.slice(0, 1).toUpperCase()}
        </span>
      )}
    </span>
  );

  return (
    <article className={styles.card}>
      {inTrash ? (
        preview
      ) : (
        <Link to={href} className={styles.cardLink} tabIndex={-1} aria-hidden="true">
          {preview}
        </Link>
      )}
      <div className={styles.cardBody}>
        <div className={styles.cardText}>
          <h3 className={styles.cardTitle} title={board.title}>
            {inTrash ? board.title : <Link to={href}>{board.title}</Link>}
          </h3>
          <p className={styles.cardMeta}>
            {board.owner.id !== me?.id && (
              <Avatar
                name={board.owner.name}
                color={board.owner.color}
                size={18}
                title={t('dashboard.by', { name: board.owner.name })}
              />
            )}
            {inTrash
              ? t('dashboard.deletedAt', { time: relativeTime(locale, board.deletedAt!) })
              : t('dashboard.edited', { time: relativeTime(locale, board.updatedAt) })}
          </p>
        </div>
        {!inTrash && (
          <IconButton
            size="sm"
            label={board.starred ? t('dashboard.unstar') : t('dashboard.star')}
            active={board.starred}
            onClick={() => actions.onStar(board)}
          >
            <Star size={16} fill={board.starred ? 'currentColor' : 'none'} />
          </IconButton>
        )}
        <Menu
          trigger={(p) => (
            <IconButton size="sm" label={t('dashboard.actions')} {...p}>
              <MoreHorizontal size={16} />
            </IconButton>
          )}
          items={items}
        />
      </div>
    </article>
  );
}
